import { type ReactNode, useState } from "react";
import { TeacherNavbar } from "./TeacherNavbar";
import { TeacherSidebar } from "./TeacherSidebar";

interface TeacherPageProps {
  title: string | null;
  /** Under the title pill — who or what the page is about, when the title alone is thin. */
  subtitle?: string | undefined;
  /** Right of the title — the page's own actions, such as Back. */
  actions?: ReactNode;
  breadcrumb?: ReactNode;
  /** The homework review needs the extra column. */
  wide?: boolean;
  /** The admin area frames these pages itself, and the teacher navbar links into /teacher, which admins cannot open. */
  embedded?: boolean;
  children: ReactNode;
}

/**
 * The shell every teacher page shares: the navbar, the section sidebar, the app's sky backdrop,
 * a frosted title pill, and white cards floating on it. Light only, like the student app it
 * mirrors: the text colour is pinned here so the app-wide dark theme cannot reach in through
 * inheritance.
 */
export function TeacherPage({
  title,
  subtitle,
  actions,
  breadcrumb,
  wide = false,
  embedded = false,
  children,
}: TeacherPageProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // The sidebar eats horizontal room, so the frame widens a step to leave the content its own.
  const width = embedded ? (wide ? "max-w-6xl" : "max-w-5xl") : wide ? "max-w-7xl" : "max-w-6xl";

  return (
    <div className="min-h-screen bg-app-sky text-app-text">
      {!embedded && <TeacherNavbar width={width} onOpenMenu={() => setIsMenuOpen(true)} />}

      <div className={`mx-auto flex gap-5 px-4 py-6 sm:px-6 sm:py-8 ${width}`}>
        {!embedded && <TeacherSidebar isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />}

        <div className="min-w-0 flex-1 space-y-5">
          {breadcrumb}
          <div className="flex items-center justify-between gap-3">
            {title && (
              <h1 className="min-w-0 truncate rounded-full border border-white/70 bg-white/60 px-4 py-2.5 font-kumbh text-lg font-bold text-app-ink shadow-app-card backdrop-blur sm:px-6 sm:py-3 sm:text-xl">
                {title}
              </h1>
            )}
            {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
          </div>
          {subtitle && (
            <p className="body-sm -mt-2 truncate pl-4 text-app-ink/70 sm:pl-6">{subtitle}</p>
          )}

          {children}
        </div>
      </div>
    </div>
  );
}
